import React, { ReactNode, useContext } from 'react';
import { FieldCompatibleProps } from '../Field';
import { HelpText } from '../HelpText';
import { Text } from '../Text';
import { BackgroundContext, LabelText } from '~/internal';
import { handleFieldsetBlur, useId } from '~/utils';
import { useFullTheme } from '~/design';

interface RadioGroupContextProps {
  isChecked: (value: string) => boolean;
  isDisabled: (disabled: boolean) => boolean;
  toggleValue: (checked: boolean, value: string) => void;
}

export const RadioGroupContext = React.createContext<RadioGroupContextProps | null>(null);
export interface RadioGroupProps extends FieldCompatibleProps<string> {
  /**
   * Use this prop if the `label` prop does not match your desired UI/UX but
   * there's another label on the screen that could label the radio group
   */
  'aria-labelledby'?: string;

  /**
   * If true, all of the radio options cannot be interacted with.
   */
  disabled?: boolean;

  /**
   * Content of the radio group, that includes at least two `<RadioGroup.Option />` items
   */
  children: React.ReactNode;
}

/**
 * RadioGroup allow the user to select a single option from a set.
 * - If you want to allow selecting multiple options at once, avoid using this component and use the CheckboxGroup component instead.
 */
export function RadioGroup({
  disabled = false,
  helpText,
  invalid = false,
  label,
  onBlur,
  onChange,
  children,
  required = false,
  value,
  'aria-labelledby': ariaLabelledBy,
}: RadioGroupProps) {
  const labelId = useId();
  const helpTextId = useId();
  const { spacing } = useFullTheme();
  const { isDisabled, isChecked, toggleValue } = useRadioGroup({ disabled, value, onChange });

  if (label && ariaLabelledBy) {
    throw new Error('Only one of label or aria-labelledby can be provided');
  }

  return (
    // This is fine to disable because we're reacting to blurs from child interactive elements
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <fieldset
      aria-describedby={helpText ? helpTextId : undefined}
      aria-labelledby={ariaLabelledBy ? ariaLabelledBy : label ? labelId : undefined}
      css={{
        display: 'flex',
        flexDirection: 'column',
        gap: spacing[2],
        border: 0,
        marginInline: 0,
        padding: 0,
      }}
      onBlur={event => handleFieldsetBlur({ event, onBlur })}
    >
      {label && (
        <LabelText
          as="legend"
          disabled={disabled}
          id={labelId}
          required={required}
          overflow="normal"
        >
          {label}
        </LabelText>
      )}

      {helpText && (
        <HelpText id={helpTextId} intent={invalid ? 'negative' : 'secondary'} showIcon={invalid}>
          {helpText}
        </HelpText>
      )}
      <RadioGroupContext.Provider
        value={{
          isDisabled,
          isChecked,
          toggleValue,
        }}
      >
        {children}
      </RadioGroupContext.Provider>
    </fieldset>
  );
}

interface RadioGroupOptionProps {
  /** If true, the radio button cannot be interacted with. */
  disabled?: boolean;

  /** The text of the radio button. Avoid adding interactive elements inside. */
  label: ReactNode | string;

  /** If true, the radio button appears as selected. */
  checked?: boolean;

  /** The value of the radio button. */
  value: string;
}

/**
 * - This component can only be used inside RadioGroup and can't be used as a standalone component.
 * - The `name` prop is not supported by design to encourage controlled components.
 */
RadioGroup.Option = function RadioGroupOption({
  disabled: _disabled = false,
  label,
  value,
}: RadioGroupOptionProps) {
  const context = React.useContext(RadioGroupContext);

  if (!context) {
    throw new Error('`RadioGroup.Option` can only be rendered inside `RadioGroup`');
  }
  const { borderRadiuses, colors, spacing } = useFullTheme();
  const { isDisabled, isChecked, toggleValue } = context;
  const disabled = isDisabled(_disabled);
  const { backgroundColor } = useContext(BackgroundContext);

  return (
    <label
      css={{
        alignItems: 'flex-start',
        cursor: disabled ? undefined : 'pointer',
        display: 'inline-flex',
        gap: spacing[2],
        minHeight: spacing[12],
        paddingTop: spacing[3],
        // legacy.min.css has several CSS rules that target the label tag
        margin: 0,
      }}
    >
      <input
        checked={isChecked(value)}
        css={{
          appearance: 'none',
          cursor: disabled ? undefined : 'pointer',
          border: `2px solid ${colors.actionSecondary}`,
          borderRadius: borderRadiuses.circle,
          flex: `0 0 ${spacing[6]}`,
          height: spacing[6],
          margin: 0,
          width: spacing[6],
          '&:checked': {
            border: `8px solid ${colors.actionPrimary}`,
          },
          '&:enabled:focus-visible': {
            outline: `2px solid ${colors.actionPrimary}`, // TODO discuss creating outline tokens
            outlineOffset: '1px',
          },
          '&:enabled:hover': {
            boxShadow: `0px 0px 0px 8px ${colors.bgSecondary}`, // TODO discuss creating shadow tokens
          },
          '&:enabled:hover:checked': {
            boxShadow: `0px 0px 0px 8px ${colors.bgPrimary}`,
          },
          '&:disabled': {
            borderColor: colors.borderSecondary,
            cursor: 'not-allowed',
          },
        }}
        disabled={disabled}
        onChange={({ target: { checked } }) => toggleValue(checked, value)}
        type="radio"
        value={value}
      />
      <Text variant="bodyM" contrast={!disabled ? 'high' : backgroundColor ? 'mid' : 'low'}>
        {label}
      </Text>
    </label>
  );
};

function useRadioGroup({
  disabled: groupDisabled,
  onChange: onCheckedValuesChange,
  value: checkedValues,
}: {
  disabled: boolean;
  onChange: (value: string) => void;
  value: string;
}) {
  const isDisabled = (disabled: boolean) => disabled || groupDisabled;

  const isChecked = (value: string) => checkedValues === value;

  const toggleValue = (checked: boolean, value: string) => {
    if (checked) onCheckedValuesChange(value);
  };

  return { isDisabled, isChecked, toggleValue };
}
